import { useCallback } from "react";

import { useLibrary } from "../../../providers";
import { useTypedRequestWithFeedback } from "../../../composites";

import { prepareIncomeSourcePayload } from "./utils";

import type {
  CreateIncomeSourceOverrideRunProps,
  EditIncomeSourceOverrideRunProps,
  IncomeReportingFormValues,
} from "./types";

export const useCreateIncomeSource = () => {
  const l10n = useLibrary("translations");
  const { send, loading } = useTypedRequestWithFeedback(
    "post",
    "/v1/income_source/",
  );

  const createIncomeSource = useCallback(
    (
      formData: IncomeReportingFormValues,
      overrideRunProps?: CreateIncomeSourceOverrideRunProps,
    ) => {
      send({
        body: prepareIncomeSourcePayload(formData),
        messaging: {
          toast: {
            success: l10n.getString(
              "income-reporting-create-success",
              null,
              "Income added.",
            ),
            error: l10n.getString(
              "income-reporting-create-error",
              null,
              "Unable to add your income. Please try again later.",
            ),
          },
        },
        ...overrideRunProps,
      });
    },
    [l10n, send],
  );

  return { createIncomeSource, loading };
};

export const useEditIncomeSource = () => {
  const l10n = useLibrary("translations");
  const { send, loading } = useTypedRequestWithFeedback(
    "put",
    "/v1/income_source/{uuid}/",
  );

  const editIncomeSource = useCallback(
    (
      formData: IncomeReportingFormValues,
      uuid: string,
      overrideRunProps?: EditIncomeSourceOverrideRunProps,
    ) => {
      send({
        params: { path: { uuid } },
        body: prepareIncomeSourcePayload(formData),
        messaging: {
          toast: {
            success: l10n.getString(
              "income-reporting-edit-success",
              null,
              "Income updated.",
            ),
            error: l10n.getString(
              "income-reporting-edit-error",
              null,
              "Unable to update your income. Please try again later.",
            ),
          },
        },
        ...overrideRunProps,
      });
    },
    [l10n, send],
  );

  return { editIncomeSource, loading };
};
